import type { CreditLimitComment, OverviewRow } from '../../types/creditLimits';

export interface EmailTemplateOutput {
    subject: string;
    plainIntro: string;
    buildBodyContent: (editedIntro: string) => string;
    buildHtmlBody: (editedIntro: string) => string;
}

/** Everything that comes from a person or from the database goes through this before it enters the HTML. */
export function escapeHtml(value: string | number | null | undefined): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatNumber(value: number | null | undefined): string {
    if (value == null) return '-';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}

function formatPercent(ratio: number | null): string {
    return ratio == null ? '-' : `${(ratio * 100).toFixed(1)} %`;
}

function plainTextToHtml(text: string): string {
    return text.split('\n').map(line => line ? `<p style="margin:0 0 8px 0;">${escapeHtml(line)}</p>` : '<br/>').join('\n');
}

function wrapHtml(body: string): string {
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/></head><body style="font-family:Arial,sans-serif;font-size:14px;color:#333;">${body}</body></html>`;
}

const CELL = 'padding:6px 8px;border:1px solid #ddd;';

function line(label: string, value: string, style = ''): string {
    return `<tr><td style="${CELL}">${escapeHtml(label)}</td><td style="${CELL}text-align:right;${style}">${value}</td></tr>`;
}

/**
 * The mail «Credit limit review» to the salesperson of a customer, from one row of the overview. Amounts are CHF
 * except the insurance limit (EUR). The editable part is the intro; table, comments and footer are fixed.
 */
export function buildCreditLimitTemplate(
    row: OverviewRow,
    comments: CreditLimitComment[],
    senderEmail: string,
): EmailTemplateOutput {
    const name = row.customerName || row.customerCode;
    const subject = `Credit Limit Review: ${name} (${row.customerCode})`;

    const plainIntro = [
        `Hi,`,
        ``,
        `I would like to bring the following customer's credit situation to your attention for review.`,
        ``,
        `Thank you.`,
    ].join('\n');

    const overdueStyle = row.overdueNetChf > 0 ? 'color:#dc2626;font-weight:bold;' : '';
    const dataBlock = `
        <table style="border-collapse:collapse;width:100%;margin:16px 0;">
            <tr style="background:#f3f4f6;">
                <th style="padding:8px;border:1px solid #ddd;text-align:left;">Field</th>
                <th style="padding:8px;border:1px solid #ddd;text-align:right;">Value</th>
            </tr>
            ${line('Customer', `${escapeHtml(name)} (${escapeHtml(row.customerCode)})`)}
            ${line('Sales Manager', escapeHtml(row.salesManagerName || '-'))}
            ${line('Payment terms', escapeHtml(row.paymentTermCode || '-'))}
            ${line('Credit Limit (CHF)', row.hasLimit ? formatNumber(row.creditLimitChf) : 'No limit')}
            ${line('Insurance Limit (EUR)', formatNumber(row.creditInsuranceEur))}
            ${line('Open Balance (CHF)', formatNumber(row.balanceChf))}
            ${line('Overdue (CHF)', formatNumber(row.overdueNetChf), overdueStyle)}
            ${line('Open Sales Orders (CHF)', formatNumber(row.salesOrdersChf))}
            ${line('Open Frame Orders (CHF)', formatNumber(row.frameOrdersChf))}
            ${line('Utilization', formatPercent(row.utilization), 'font-weight:bold;')}
            ${line('Preview incl. open orders', formatPercent(row.preview))}
        </table>
    `;

    const commentsBlock = comments.length > 0
        ? `<h3 style="margin:16px 0 8px 0;font-size:14px;">Comments</h3>
           <ul style="margin:0;padding-left:20px;">
               ${comments.map(c => `<li style="margin-bottom:4px;"><strong>${escapeHtml(c.CommentType)}:</strong> ${escapeHtml(c.CommentText)} <em style="color:#6b7280;">(${escapeHtml(c.CreatedBy)})</em></li>`).join('\n')}
           </ul>`
        : '';

    const footer = `<p style="margin-top:24px;color:#6b7280;font-size:12px;">Sent from the Dolder Reporting App by ${escapeHtml(senderEmail)}</p>`;

    const buildBodyContent = (editedIntro: string) =>
        [plainTextToHtml(editedIntro), dataBlock, commentsBlock, footer].filter(Boolean).join('\n');

    return {
        subject,
        plainIntro,
        buildBodyContent,
        buildHtmlBody: (editedIntro: string) => wrapHtml(buildBodyContent(editedIntro)),
    };
}
